import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import NavigationDropdown from './NavigationDropdown';
import { getAllModulesWithProgress } from '../../selectors/progress';
import { getDischargeProgress } from '../../selectors/dischargeProgress';

// --- Helper Functions ---

const findItemByPathIndex = (items, pathArray, keyword, offset = -1) => {
  if (!items || !pathArray.includes(keyword)) return null;
  const index = pathArray.indexOf(keyword) + offset;
  const id = parseInt(pathArray[index], 10);
  return items[id - 1] || null;
};

const LearnNavigation = ({ location: { pathname }, isDischarge }) => {
  const node = useRef();
  
  // 1. Unified Menu State (mutually exclusive)
  const [activeMenu, setActiveMenu] = useState(null); // 'chapters' | 'lessons' | 'topics' | null

  // 2. Redux Selectors
  const modules = useSelector(getAllModulesWithProgress);
  const dischargeSteps = useSelector(getDischargeProgress);

  // 3. Derived State: URL Parsing
  const { 
    pathnameArray, isChapterPage, isLessonPage, 
    currentModule, currentLessons, currentLesson, currentTopics 
  } = useMemo(() => {
    const parts = pathname.split('/');
    const isChapter = parts.includes('learn') && parts.length > 2;
    const isLesson = parts.includes('lesson');
    
    // Determine the relevant item set
    const dataSet = isDischarge ? dischargeSteps : modules;
    
    // Logic to extract current items based on path keywords
    const mod = findItemByPathIndex(dataSet, parts, 'lessons', -1) 
             || findItemByPathIndex(dataSet, parts, 'pages', -1) 
             || dataSet?.[parseInt(parts[parts.length - 1], 10) - 1];

    const lessons = mod?.lessons || mod?.topics || [];
    const lesson = findItemByPathIndex(lessons, parts, 'lesson', 1); // some paths have lesson ID after the word
    const topics = lesson?.topics || [];

    return { 
      pathnameArray: parts, 
      isChapterPage: isChapter, 
      isLessonPage: isLesson,
      currentModule: mod,
      currentLessons: lessons,
      currentLesson: lesson,
      currentTopics: topics
    };
  }, [pathname, isDischarge, modules, dischargeSteps]);

  // 4. Handlers
  const toggleMenu = useCallback((menuName) => {
    setActiveMenu(prev => (prev === menuName ? null : menuName));
  }, []);

  const closeAll = useCallback(() => setActiveMenu(null), []);

  // 5. Side-Effects: Click Outside & Body Class
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (node.current && !node.current.contains(e.target)) closeAll();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeAll]);

  useEffect(() => {
    const bodyClass = 'navigation-dropdown--open';
    if (activeMenu) {
      document.body.classList.add(bodyClass);
    } else {
      document.body.classList.remove(bodyClass);
    }
    return () => document.body.classList.remove(bodyClass);
  }, [activeMenu]);

  return (
    <div className="header__secondary-nav-wrapper" ref={node}>
      {/* CHAPTERS / STEPS */}
      <div className="header__secondary-item header__secondary-item--link">
        <NavigationDropdown
          itemType={!isDischarge ? 'module' : 'discharge'}
          items={isDischarge ? dischargeSteps : modules}
          navigationTitle={isDischarge ? 'Steps' : 'Chapters'}
          expanded={activeMenu === 'chapters'}
          expandHandler={() => toggleMenu('chapters')}
        />
      </div>

      {/* LESSONS */}
      {isChapterPage && (
        <div className="header__secondary-item header__secondary-item--link">
          <NavigationDropdown
            parentSlug={pathnameArray[pathnameArray.length - 1]}
            itemType="lesson"
            currentModule={currentModule}
            items={currentLessons}
            navigationTitle="Lessons"
            expanded={activeMenu === 'lessons'}
            expandHandler={() => toggleMenu('lessons')}
          />
        </div>
      )}

      {/* TOPICS (Standard or Discharge) */}
      {(isLessonPage || (isDischarge && currentLessons?.length > 0)) && (
        <div className="header__secondary-item header__secondary-item--link">
          <NavigationDropdown
            parentSlug={pathnameArray[pathnameArray.length - 1]}
            itemType={isDischarge ? "discharge-topics" : "topic"}
            currentDischargeStep={isDischarge ? currentModule : undefined}
            items={isDischarge ? currentLessons : currentTopics}
            navigationTitle="Topics"
            expanded={activeMenu === 'topics'}
            expandHandler={() => toggleMenu('topics')}
          />
        </div>
      )}
    </div>
  );
};

LearnNavigation.propTypes = {
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
  isDischarge: PropTypes.bool.isRequired
};

export default React.memo(LearnNavigation);
